<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('limits_settings')"
    :subtitle="`ID: ${limit.id}`"
    icon="mdi-cog-outline"
    color="deep-purple"
  >
    <v-stepper v-model="currentStep" flat>
      <v-stepper-header class="ordera-box-shadow">
        <template v-for="(step, index) in visibleSteps">
          <v-stepper-step
            :complete="currentStep > step.number"
            :step="step.number"
            :editable="currentStep > step.number"
          >
            {{ step.title }}
            <small v-if="step.subtitle">{{ step.subtitle }}</small>
          </v-stepper-step>
          <v-divider v-if="index < visibleSteps.length - 1" :key="`divider-${index}`" />
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content
          v-for="step in visibleSteps"
          :key="`content-${step.number}`"
          :step="step.number"
          class="pa-1"
        >
          <component
            :is="step.component"
            :config="limit.params"
            :limit="limit"
            :limit-id="limit?.id"
            :readonly="readonly"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <template #footer-actions>
      <v-btn text @click="currentStep = currentStep - 1" v-if="currentStep > 1">
        <v-icon left>mdi-arrow-left</v-icon>
        {{ $t("back") }}
      </v-btn>
      <v-spacer />
      <primary-button
        v-if="currentStep < visibleSteps.length"
        @click="currentStep = currentStep + 1"
      >
        {{ $t("continue") }}
        <v-icon right>mdi-arrow-right</v-icon>
      </primary-button>
      <primary-button
        v-else
        @click="saveConfiguration"
        :loading="saving"
        :disabled="readonly"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ $t("save_configuration") }}
      </primary-button>
    </template>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import limitService from "@/services/limit";
import { getDefaultLimitConfig } from "@/models/limitConfiguration";
import {
  getLimitTypeLabelKey,
  getTimeLimitLabelKey,
  getWhoPaysLabelKey,
  getWhatCanOrderLabelKey,
  getOverLimitLabelKey,
} from "@/utils/limitHelpers";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import Step1Combined from "@/components/limitConfiguration/steps/Step1Combined.vue";
import Step2WhatCanOrder from "@/components/limitConfiguration/steps/Step2WhatCanOrder.vue";
import Step3WhoPays from "@/components/limitConfiguration/steps/Step3WhoPays.vue";
import Step4OverLimit from "@/components/limitConfiguration/steps/Step4OverLimit.vue";
import Step5RestaurantsSelection from "@/components/limitConfiguration/steps/Step5RestaurantsSelection.vue";
import Step5Overview from "@/components/limitConfiguration/steps/Step5Overview.vue";

export default {
  name: "LimitConfigurationDialog",
  components: {
    FullscreenDialog,
    Step1Combined,
    Step2WhatCanOrder,
    Step3WhoPays,
    Step4OverLimit,
    Step5RestaurantsSelection,
    Step5Overview,
  },
  mixins: [hasModel, hasSnackbar],
  props: {
    limit: {
      type: Object,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStep: 1,
      saving: false,
    };
  },
  computed: {
    steps() {
      return [
        {
          number: 1,
          title: this.$t("limit_type_and_time_limit"),
          component: "Step1Combined",
          getSubtitle: (config) => {
            const limitType = config.limit_definition?.active
              ? this.getLimitTypeLabel(config.limit_definition.active)
              : null;
            const timeLimit = config.timing?.active
              ? this.getTimeLimitLabel(config.timing.active)
              : null;
            if (limitType && timeLimit) {
              return `${limitType} / ${timeLimit}`;
            }
            return limitType || timeLimit || null;
          },
        },
        {
          number: 2,
          title: this.$t("what_can_order"),
          component: "Step2WhatCanOrder",
          getSubtitle: (config) =>
            config.ordering_rules?.active
              ? this.getWhatCanOrderLabel(config.ordering_rules.active)
              : null,
        },
        {
          number: 3,
          title: this.$t("who_pays_title"),
          component: "Step3WhoPays",
          getSubtitle: (config) =>
            config.payment_logic?.active
              ? this.getWhoPaysLabel(config.payment_logic.active)
              : null,
        },
        {
          number: 4,
          title: this.$t("over_limit_title"),
          component: "Step4OverLimit",
          visible: (config) => config.limit_definition?.active !== "unlimited",
          getSubtitle: (config) => {
            const blockOrder = config.rollover_policy?.block_order;
            return blockOrder !== undefined ? this.getOverLimitLabel(blockOrder) : null;
          },
        },
        {
          number: 5,
          title: this.$t("restaurant_selection"),
          component: "Step5RestaurantsSelection",
          getSubtitle: () => {
            const restaurants = this.limit?.restaurants;
            if (!restaurants || restaurants.length === 0) return null;
            return `${restaurants.length} ${this.$t("selected")}`;
          },
        },
        {
          number: 6,
          title: this.$t("overview"),
          component: "Step5Overview",
          visible: () => true,
        },
      ];
    },
    visibleSteps() {
      const configToUse = this.limit?.params || this.config;
      return this.steps
        .filter((step) => !step.visible || step.visible(configToUse))
        .map((step, index) => ({
          ...step,
          number: index + 1,
          subtitle: step.getSubtitle ? step.getSubtitle(configToUse) : null,
        }));
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.currentStep = 1;
      }
    },
  },
  methods: {
    /**
     * Prepare a clean payload for sending
     */
    prepareCleanParams() {
      const params = this.limit.params;
      const defaults = getDefaultLimitConfig();
      const type = params.limit_definition.active;

      // If unlimited, return defaults for everything except active
      if (type === "unlimited") {
        return {
          ...defaults,
          limit_definition: {
            active: type,
          },
        };
      }

      const limitDefinition = this.buildLimitDefinition(params, type);
      const paymentLogic = this.buildPaymentLogic(params);
      const rolloverPolicy = this.buildRolloverPolicy(params);
      const timing = this.buildTiming(params);
      const orderingRules = this.buildOrderingRules(params, defaults);
      // Start from params and reset only unnecessary parts to defaults
      return {
        ...params,
        timing,
        limit_definition: limitDefinition,
        ordering_rules: orderingRules,
        payment_logic: paymentLogic,
        rollover_policy: rolloverPolicy,
      };
    },
    buildOrderingRules(params, defaults) {
      const orderingRules = params.ordering_rules || {};
      const active = Array.isArray(orderingRules.active)
        ? orderingRules.active
        : orderingRules.active
        ? [orderingRules.active]
        : [];

      const normalizedStrictCompositionSource =
        orderingRules.settings.strict_composition || {};

      const normalizedStrictComposition = Object.entries(
        normalizedStrictCompositionSource
      ).reduce((acc, [categoryId, value]) => {
        if (value === "" || value === null || value === undefined) {
          return acc;
        }

        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
          acc[String(categoryId)] = numericValue;
        }

        return acc;
      }, {});

      const isGroupSelectionEnabled = active.includes("group_selection");
      let settings = {
        is_strict_composition: orderingRules.settings.is_strict_composition,
      };
      if (isGroupSelectionEnabled) {
        if (settings.is_strict_composition) {
          settings["strict_composition"] = normalizedStrictComposition;
        } else {
          settings["meals_per_group"] = orderingRules.settings.meals_per_group;
        }
      }
      return {
        active,
        settings,
      };
    },
    buildTiming(params) {
      const timing = params.timing || {};
      const settings = timing.settings || {};
      const active = timing.active ?? null;

      const settingsByType = {
        day: () => ({ reset_time: settings.reset_time ?? null }),
        week: () => ({ week_start: settings.week_start ?? null }),
        month: () => ({ reset_day: settings.reset_day ?? null }),
        year: () => ({
          reset_month: settings.reset_month ?? null,
          reset_day: settings.reset_day ?? null,
        }),
        custom: () => ({
          start_date: settings.start_date ?? null,
          end_date: settings.end_date ?? null,
        }),
        shift: () => ({}),
        no_limit: () => ({}),
      };

      return {
        active,
        settings: settingsByType[active] ? settingsByType[active]() : {},
      };
    },
    buildLimitDefinition(params, type) {
      const { settings } = params.limit_definition;
      const shiftLimits = this.buildShiftLimits(settings, type, params.timing?.active);
      const settingsByType = {
        order: () => {
          const orderSettings = {};
          orderSettings.limit_by_quantity = settings.limit_by_quantity;
          orderSettings.limit_by_value = settings.limit_by_value;
          if (settings.limit_by_quantity) {
            orderSettings.max_orders = settings.max_orders;
          }
          if (settings.limit_by_value) {
            orderSettings.max_order_value = settings.max_order_value;
          }
          if (shiftLimits) {
            orderSettings.shift_limits = shiftLimits;
          }
          return orderSettings;
        },
        voucher: () => ({
          value_per_voucher: settings.value_per_voucher,
          currency: settings.currency,
          orders_per_voucher: settings.orders_per_voucher,
          quantity_in_period: settings.quantity_in_period,
          apply_to_all_shifts: settings.apply_to_all_shifts,
          ...(shiftLimits ? { shift_limits: shiftLimits } : {}),
        }),
        credit: () => ({
          max_amount: settings.max_amount,
          allow_negative: settings.allow_negative,
          ...(shiftLimits ? { shift_limits: shiftLimits } : {}),
        }),
        money: () => ({
          limit_amount: settings.limit_amount,
          ...(shiftLimits ? { shift_limits: shiftLimits } : {}),
        }),
      };

      const buildSettings = settingsByType[type];
      return {
        active: type,
        ...(buildSettings ? { settings: buildSettings() } : {}),
      };
    },
    buildShiftLimits(settings, type, timingActive) {
      if (timingActive !== "shift") return null;
      if (settings.apply_to_all_shifts !== false) return null;

      const shiftLimits = {};

      if (type === "voucher") {
        if (settings.shift_limits?.quantity_in_period) {
          shiftLimits.quantity_in_period = settings.shift_limits.quantity_in_period;
        }
      }

      if (type === "order") {
        if (settings.limit_by_quantity && settings.shift_limits?.max_orders) {
          shiftLimits.max_orders = settings.shift_limits.max_orders;
        }
        if (settings.limit_by_value && settings.shift_limits?.max_order_value) {
          shiftLimits.max_order_value = settings.shift_limits.max_order_value;
        }
      }

      if (type === "credit") {
        if (settings.shift_limits?.max_amount) {
          shiftLimits.max_amount = settings.shift_limits.max_amount;
        }
      }

      if (type === "money") {
        if (settings.shift_limits?.limit_amount) {
          shiftLimits.limit_amount = settings.shift_limits.limit_amount;
        }
      }

      return Object.keys(shiftLimits).length ? shiftLimits : null;
    },
    buildPaymentLogic(params) {
      const paymentType = params.payment_logic.active;
      const { settings } = params.payment_logic;

      const settingsByType = {
        company_only: () => ({
          company_share: 100,
          user_share: 0,
        }),
        user_only: () => ({
          company_share: 0,
          user_share: 100,
        }),
        split: () => {
          if (settings.split_by_percentage) {
            return {
              split_by_percentage: true,
              company_share: settings.company_share,
              user_share: settings.user_share,
            };
          }
          return {
            split_by_percentage: false,
            threshold: settings.threshold,
          };
        },
        company_payroll_deduct: () => ({
          currency: settings.currency,
          monthly_cap: settings.monthly_cap,
        }),
      };

      const buildSettings = settingsByType[paymentType];
      return {
        active: paymentType,
        ...(buildSettings ? { settings: buildSettings() } : {}),
      };
    },
    buildRolloverPolicy(params) {
      const rolloverPolicy = {
        allow_transfer: params.rollover_policy.allow_transfer,
        block_order: params.rollover_policy.block_order,
        settings: {},
      };
      const { settings } = params.rollover_policy;
      if (rolloverPolicy.allow_transfer) {
        rolloverPolicy.settings.transfer_period = settings.transfer_period;
        rolloverPolicy.settings.transfer_limit_value = settings.transfer_limit_value;
      }
      if (!rolloverPolicy.block_order) {
        rolloverPolicy.settings.carry_debt_to_next_month =
          settings.carry_debt_to_next_month;
        rolloverPolicy.settings.notification_settings = settings.notification_settings;
        if (settings.notification_settings?.notify_user) {
          rolloverPolicy.settings.notification_settings.message =
            settings.notification_settings.message;
        }
        if (settings.carry_debt_to_next_month) {
          rolloverPolicy.settings.max_debt_value = settings.max_debt_value;
        }
      }

      return rolloverPolicy;
    },

    async saveConfiguration() {
      this.saving = true;
      try {
        if (!this.limit?.id) {
          console.error("Limit ID is required for saving configuration");
          this.showErrorSnackbar(this.$t("error_updating_limit"));
          return;
        }

        // Prepare a clean payload
        const cleanParams = this.prepareCleanParams();

        await limitService.updateLimitParams(this.limit.id, {
          ...this.limit,
          params: cleanParams,
        });

        this.showSuccessSnackbar(this.$t("limit_updated_successfully"));
        this.$emit("saved");
        this.model = false;
      } catch (error) {
        console.error("Error saving limit configuration:", error);
        this.showErrorSnackbar(this.$t("error_updating_limit"));
      } finally {
        this.saving = false;
      }
    },
    getLimitTypeLabel(type) {
      const key = getLimitTypeLabelKey(type);
      return key ? this.$t(key) : "";
    },
    getTimeLimitLabel(type) {
      const key = getTimeLimitLabelKey(type);
      return key ? this.$t(key) : "";
    },
    getWhoPaysLabel(type) {
      const key = getWhoPaysLabelKey(type);
      return key ? this.$t(key) : "";
    },
    getWhatCanOrderLabel(type) {
      if (Array.isArray(type)) {
        if (type.length === 0) return "";
        return type
          .map((t) => {
            const key = getWhatCanOrderLabelKey(t);
            return key ? this.$t(key) : "";
          })
          .filter(Boolean)
          .join(", ");
      }
      const key = getWhatCanOrderLabelKey(type);
      return key ? this.$t(key) : "";
    },
    getOverLimitLabel(blockOrder) {
      const key = getOverLimitLabelKey(blockOrder);
      return key ? this.$t(key) : "";
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-header {
  font-size: 1.1rem;
}
</style>
